import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../common/Layout'
import { getLifeEvents } from '../../services/api'

// Category configuration
const MILESTONE_CATEGORIES = [
  { id: 'education', emoji: '🎓', name: 'Education', color: '#3B82F6', examples: 'Graduations, Degrees, Schools' },
  { id: 'career', emoji: '💼', name: 'Career', color: '#10B981', examples: 'Jobs, Promotions, Retirement' },
  { id: 'family', emoji: '👨‍👩‍👧‍👦', name: 'Family', color: '#EC4899', examples: 'Weddings, Births, Reunions' },
  { id: 'home', emoji: '🏠', name: 'Home', color: '#F59E0B', examples: 'First Home, Moves, Renovations' },
  { id: 'travel', emoji: '✈️', name: 'Travel', color: '#8B5CF6', examples: 'Vacations, Adventures, Trips' },
  { id: 'achievement', emoji: '🏆', name: 'Achievement', color: '#EF4444', examples: 'Awards, Goals, Personal Bests' }
]

const MEMORY_OPTIONS = [
  { id: 'write', emoji: '✍️', name: 'Write a Story', color: '#556B2F', description: 'Type your memory in your own words' },
  { id: 'audio', emoji: '🎙️', name: 'Record Audio', color: '#E65100', description: 'Tell your story in your own voice' },
  { id: 'photos', emoji: '📷', name: 'Add Photos', color: '#6B8E23', description: 'Upload and describe your photos' }
]

function MyLifeHubV2() {
  const navigate = useNavigate()
  const [lifeEvents, setLifeEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [activeTab, setActiveTab] = useState('milestones')
  const [showCategoryModal, setShowCategoryModal] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const events = await getLifeEvents()
        setLifeEvents(events)
        setLoading(false)
      } catch (err) {
        console.error('Error fetching life events:', err)
        setError('Failed to load life events')
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSelectedCategory('all')
    // Stay on the same page, just switch tabs
  }

  // Filter events based on active tab
  const milestones = lifeEvents.filter(event =>
    event.template !== 12 && event.category !== 'Stories'
  )

  const memories = lifeEvents.filter(event =>
    event.template === 12 || event.category === 'Stories' || event.event_data?.story_type
  )

  // Filter milestones by category
  const filteredMilestones = selectedCategory === 'all'
    ? milestones
    : milestones.filter(m => {
        const categoryName = m.category?.toLowerCase()
        const eventDataCategory = m.event_data?.category?.toLowerCase()
        return categoryName === selectedCategory || eventDataCategory === selectedCategory
      })

  // Calculate stats
  const stats = {
    milestones: milestones.length,
    memories: memories.length,
    photos: lifeEvents.filter(e => e.event_data?.hasPhotos || e.photos?.length > 0).length
  }

  return (
    <Layout>
      <div className="bg-[#F5F5DC] min-h-screen">
        <div className="max-w-[1440px] mx-auto p-6">
          {/* Page Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl font-bold text-[#556B2F] mb-2">My Life</h1>
              <p className="text-lg text-[#666666]">Record your journey and preserve your legacy</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCategoryModal(true)}
                className="flex items-center gap-2 bg-[#556B2F] text-white px-5 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity"
              >
                🏆 Add Milestone
              </button>
              <button
                onClick={() => navigate('/tell-a-story')}
                className="flex items-center gap-2 bg-[#E65100] text-white px-5 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity"
              >
                ❤️ Add Memory
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border border-[#CCCCCC] rounded-xl overflow-hidden mb-6 inline-flex w-full max-w-md">
            <button
              onClick={() => handleTabChange('milestones')}
              className={`flex-1 px-8 py-4 text-lg font-semibold transition-colors ${
                activeTab === 'milestones'
                  ? 'bg-[#556B2F] text-white'
                  : 'bg-white text-[#333333]'
              }`}
            >
              📍 Milestones
            </button>
            <button
              onClick={() => handleTabChange('memories')}
              className={`flex-1 px-8 py-4 text-lg font-semibold transition-colors ${
                activeTab === 'memories'
                  ? 'bg-[#556B2F] text-white'
                  : 'bg-white text-[#333333]'
              }`}
            >
              💭 Memories
            </button>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            {/* Main Content Area */}
            <div>
              {loading ? (
                <div className="text-center py-12 bg-white rounded-2xl">
                  <div className="text-6xl mb-4">⏳</div>
                  <p className="text-lg text-[#666666]">Loading your life events...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12 bg-white rounded-2xl">
                  <div className="text-6xl mb-4">😕</div>
                  <p className="text-lg text-red-500">{error}</p>
                </div>
              ) : (
                <>
                  {activeTab === 'milestones' ? (
                    <MilestonesContent
                      milestones={filteredMilestones}
                      allMilestones={milestones}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                    />
                  ) : (
                    <MemoriesContent memories={memories} />
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <Sidebar stats={stats} onAddMilestone={() => setShowCategoryModal(true)} />
          </div>
        </div>

        {/* Category Selection Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowCategoryModal(false)}>
            <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-[#556B2F] mb-2">Add a Milestone</h2>
                  <p className="text-lg text-[#666666]">Choose a category to get started</p>
                </div>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-3xl text-[#666666] hover:text-[#333333] leading-none"
                >
                  ×
                </button>
              </div>

              {/* Category Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {MILESTONE_CATEGORIES.map(category => (
                  <CategoryCard key={category.id} category={category} onClick={() => setShowCategoryModal(false)} />
                ))}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-[#CCCCCC]" />
                <span className="text-sm text-[#666666]">or</span>
                <div className="flex-1 h-px bg-[#CCCCCC]" />
              </div>

              {/* Custom Milestone Button */}
              <button
                onClick={() => {
                  setShowCategoryModal(false)
                  navigate('/lifeevent/create-generic')
                }}
                className="w-full bg-[#E65100] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity"
              >
                ➕ Create Custom Milestone
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

// Milestones Content Component
function MilestonesContent({ milestones, allMilestones, selectedCategory, setSelectedCategory }) {
  if (allMilestones.length === 0) {
    return <MilestonesEmptyState />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="text-4xl">📍</div>
        <div>
          <h2 className="text-2xl font-bold text-[#556B2F]">Life Milestones</h2>
          <p className="text-base text-[#666666]">Important moments and achievements in your life journey</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === 'all'
              ? 'bg-[#556B2F] text-white'
              : 'bg-[#F2F2F2] text-[#333333] hover:bg-[#E8F0E0]'
          }`}
        >
          All
        </button>
        {MILESTONE_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === cat.id
                ? 'bg-[#556B2F] text-white'
                : 'bg-[#F2F2F2] text-[#333333] hover:bg-[#E8F0E0]'
            }`}
          >
            {cat.emoji} {cat.name}
          </button>
        ))}
      </div>

      {/* Milestones List */}
      <div className="space-y-4">
        {milestones.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#CCCCCC]">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-lg text-[#666666]">No milestones in this category yet</p>
          </div>
        ) : (
          milestones.map(milestone => (
            <MilestoneCard key={milestone.id} milestone={milestone} />
          ))
        )}
      </div>
    </div>
  )
}

// Milestones Empty State
function MilestonesEmptyState() {
  const navigate = useNavigate()

  return (
    <div className="bg-white rounded-2xl border border-[#CCCCCC] p-12">
      <div className="text-center max-w-3xl mx-auto">
        <div className="text-7xl mb-6">📍</div>
        <h2 className="text-3xl font-bold text-[#556B2F] mb-4">Start Your Life Timeline</h2>
        <p className="text-lg text-[#666666] mb-8 max-w-2xl mx-auto">
          Record the important moments that shaped who you are. Choose a category to begin:
        </p>

        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {MILESTONE_CATEGORIES.map(category => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-[#CCCCCC]" />
          <span className="text-sm text-[#666666]">or</span>
          <div className="flex-1 h-px bg-[#CCCCCC]" />
        </div>

        {/* Custom Milestone Button */}
        <button
          onClick={() => navigate('/lifeevent/create-generic')}
          className="bg-[#E65100] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          ➕ Create Custom Milestone
        </button>
      </div>
    </div>
  )
}

// Category Card Component
function CategoryCard({ category, onClick }) {
  const navigate = useNavigate()

  const handleClick = () => {
    // Navigate to the appropriate wizard based on category
    navigate(`/my-life-v2/add-${category.id}-milestone`)
    // Call the onClick callback if provided (to close modal)
    if (onClick) onClick()
  }

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-2xl p-5 border-2 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
      style={{ borderColor: `${category.color}66` }}
    >
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl mb-3 mx-auto"
        style={{ backgroundColor: `${category.color}33` }}
      >
        {category.emoji}
      </div>
      <h3 className="text-lg font-bold text-[#333333] mb-1">{category.name}</h3>
      <p className="text-xs text-[#666666] mb-3 min-h-[2.5rem]">{category.examples}</p>
      <div className="text-sm font-semibold" style={{ color: category.color }}>
        Add {category.name} →
      </div>
    </div>
  )
}

// Milestone Card Component
function MilestoneCard({ milestone }) {
  const navigate = useNavigate()

  // Try to find category by name first, then by ID from event_data
  const category = MILESTONE_CATEGORIES.find(c =>
    c.name.toLowerCase() === milestone.category?.toLowerCase() ||
    c.id === milestone.event_data?.category?.toLowerCase()
  )

  const formattedDate = new Date(milestone.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <div
      onClick={() => navigate(`/stories/${milestone.id}`)}
      className="bg-white rounded-xl p-5 border-l-4 hover:shadow-lg transition-shadow cursor-pointer flex items-center gap-4"
      style={{ borderLeftColor: category?.color || '#6B8E23' }}
    >
      {/* Icon */}
      <div className="w-14 h-14 rounded-xl bg-[#E8F0E0] flex items-center justify-center text-3xl flex-shrink-0">
        {category?.emoji || '📌'}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-xl font-bold text-[#333333] mb-1">{milestone.title}</h3>
        <div className="flex items-center gap-4 text-sm text-[#666666] mb-2">
          <span>📅 {formattedDate}</span>
          {milestone.location && <span>📍 {milestone.location}</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {milestone.category && (
            <span className="px-3 py-1 bg-[#E8F0E0] text-[#556B2F] rounded-xl text-xs font-medium">
              {milestone.category}
            </span>
          )}
          {milestone.event_data?.story_type ? (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-xl text-xs font-medium">
              📖 Story attached
            </span>
          ) : (
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-xl text-xs font-medium cursor-pointer hover:bg-orange-200">
              ✏️ Add story
            </span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <div className="text-2xl text-[#666666] flex-shrink-0">→</div>
    </div>
  )
}

// Memories Content Component
function MemoriesContent({ memories }) {
  if (memories.length === 0) {
    return <MemoriesEmptyState />
  }

  // Calculate how many placeholder cards to show (at least 3, up to fill a row)
  const placeholderCount = Math.max(3, 6 - (memories.length % 3))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="text-4xl">💭</div>
        <div>
          <h2 className="text-2xl font-bold text-[#556B2F]">My Memories</h2>
          <p className="text-base text-[#666666]">Stories, photos, and moments you want to preserve</p>
        </div>
      </div>

      {/* Memory Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {memories.map(memory => (
          <MemoryCard key={memory.id} memory={memory} />
        ))}
        {/* Add multiple placeholder cards like in the mockup */}
        {Array.from({ length: placeholderCount }).map((_, index) => (
          <AddMemoryCard key={`placeholder-${index}`} />
        ))}
      </div>
    </div>
  )
}

// Memories Empty State
function MemoriesEmptyState() {
  const navigate = useNavigate()

  return (
    <div className="bg-white rounded-2xl border border-[#CCCCCC] p-12">
      <div className="text-center max-w-3xl mx-auto">
        <div className="text-7xl mb-6">💭</div>
        <h2 className="text-3xl font-bold text-[#556B2F] mb-4">Share Your Stories & Memories</h2>
        <p className="text-lg text-[#666666] mb-8">
          Preserve the moments that matter most. Choose how you want to share:
        </p>

        {/* Memory Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {MEMORY_OPTIONS.map(option => (
            <div
              key={option.id}
              onClick={() => navigate('/tell-a-story')}
              className="bg-white rounded-2xl p-6 border-2 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
              style={{ borderColor: `${option.color}66` }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-4xl mb-4 mx-auto"
                style={{ backgroundColor: `${option.color}33` }}
              >
                {option.emoji}
              </div>
              <h3 className="text-lg font-bold text-[#333333] mb-2">{option.name}</h3>
              <p className="text-sm text-[#666666]">{option.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Memory Card Component
function MemoryCard({ memory }) {
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/stories/${memory.id}`)}
      className="bg-white rounded-xl border border-[#CCCCCC] overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
    >
      {/* Image area */}
      <div className="h-24 bg-[#E8F0E0] flex items-center justify-center text-4xl">
        👨‍👩‍👧‍👦
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base font-bold text-[#333333] mb-1 truncate">{memory.title}</h3>
        <p className="text-sm text-[#666666] mb-2">{memory.category || 'Memory'}</p>
        <div className="flex gap-2 flex-wrap">
          {memory.event_data?.hasPhotos && (
            <span className="px-2 py-1 bg-[#F2F2F2] text-[#333333] rounded-lg text-xs">
              📷 Photos
            </span>
          )}
          {memory.event_data?.hasAudio && (
            <span className="px-2 py-1 bg-[#F2F2F2] text-[#333333] rounded-lg text-xs">
              🎙️ Audio
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Add Memory Placeholder Card
function AddMemoryCard() {
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate('/tell-a-story')}
      className="bg-white rounded-xl border-2 border-dashed border-[#CCCCCC] min-h-[180px] flex flex-col items-center justify-center p-6 cursor-pointer hover:border-[#556B2F] transition-colors"
    >
      <div className="w-12 h-12 rounded-full bg-[#E8F0E0] flex items-center justify-center text-2xl text-[#556B2F] mb-3">
        +
      </div>
      <h3 className="text-base font-semibold text-[#556B2F] mb-1">Add a Memory</h3>
      <p className="text-sm text-[#666666]">Share a story</p>
    </div>
  )
}

// Sidebar Component
function Sidebar({ stats, onAddMilestone }) {
  const navigate = useNavigate()

  return (
    <div className="space-y-6">
      {/* Quick Actions Card */}
      <div className="bg-white rounded-2xl border border-[#CCCCCC] p-5">
        <h3 className="text-lg font-bold text-[#333333] mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <button
            onClick={onAddMilestone}
            className="w-full bg-[#556B2F] text-white px-4 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            🏆 Add Milestone
          </button>
          <button
            onClick={() => navigate('/tell-a-story')}
            className="w-full bg-[#6B8E23] text-white px-4 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            ✍️ Write a Story
          </button>
          <button
            onClick={() => navigate('/tell-a-story')}
            className="w-full bg-[#556B2F] text-white px-4 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            📷 Upload Photos
          </button>
          <button
            onClick={() => navigate('/tell-a-story')}
            className="w-full bg-[#E65100] text-white px-4 py-3 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            🎙️ Record Audio
          </button>
        </div>
      </div>

      {/* Progress Stats Card */}
      <div className="bg-white rounded-2xl border border-[#CCCCCC] p-5">
        <h3 className="text-lg font-bold text-[#333333] mb-4">Your Progress</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#E8F0E0] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#556B2F]">{stats.milestones}</div>
            <div className="text-xs text-[#666666]">Milestones</div>
          </div>
          <div className="bg-[#FFF3E8] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#E65100]">{stats.memories}</div>
            <div className="text-xs text-[#666666]">Memories</div>
          </div>
          <div className="bg-[#E8F0E0] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#6B8E23]">{stats.photos}</div>
            <div className="text-xs text-[#666666]">Photos</div>
          </div>
        </div>
      </div>

      {/* Today's Prompt Card */}
      <div className="bg-[#FFF3E8] rounded-2xl border-2 border-[#E65100] p-5">
        <h3 className="text-base font-bold text-[#E65100] mb-3">✨ Today's Prompt</h3>
        <p className="text-base text-[#333333] mb-4 font-medium">
          "What's a tradition from your childhood you'd like to pass down?"
        </p>
        <button
          onClick={() => navigate('/tell-a-story')}
          className="w-full bg-[#E65100] text-white px-4 py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Answer This Prompt
        </button>
      </div>

      {/* Tips Card */}
      <div className="bg-white rounded-2xl border border-[#CCCCCC] p-5">
        <h3 className="text-lg font-bold text-[#333333] mb-4">💡 Tips for Getting Started</h3>
        <ul className="space-y-2 text-sm text-[#666666]">
          <li className="flex items-start gap-2">
            <span className="text-[#556B2F] flex-shrink-0">•</span>
            <span>Start with your biggest life moments</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#556B2F] flex-shrink-0">•</span>
            <span>Add photos to bring stories to life</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#556B2F] flex-shrink-0">•</span>
            <span>Record audio for a personal touch</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#556B2F] flex-shrink-0">•</span>
            <span>Your family will love hearing your voice</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

export { MyLifeHubV2 }
